import { DateTime } from 'luxon';
import {
  IDaySchedule,
  IDynamicMappedShiftResponse,
  IMappedShiftResponse,
  IPublishedShiftResponse,
  ITotals,
  ShiftCategory,
} from 'mappers/scheduling/schedule.rest.types';

export const TYPE_SHARED_TEAM_MEMBER = 'shared_team_member';
export const TYPE_INTERNAL_TEAM_MEMBER = 'internal_team_member';
export const TYPE_PUBLISHED_UNASSIGNED = 'published_unassigned';
export const TYPE_AGENCY_MEMBER = 'agency_member';
export const TYPE_PUBLISHED_OTHER_POSITION = 'published_other_position';

export interface IPublishedShiftsDayData {
  data: { [key: string]: IMappedShiftResponse };
  totals: ITotals;
}

export interface IPublishedShiftsJaneData {
  data: { [key: string]: IDynamicMappedShiftResponse };
  totals: ITotals;
}

function emptyTotals(): ITotals {
  return {
    statuses: {
      open: 0,
      pending: 0,
      assigned: 0,
      in_progress: 0,
      accepted: 0,
      completed: 0,
      unfulfilled: 0,
      cancelled: 0,
      swapped: 0,
      absent: 0,
    },
    sent_to_agencies: 0,
  };
}

function dayArrToDayMap(
  arr: IDaySchedule[],
  _type: ShiftCategory,
): { data: { [key: string]: IMappedShiftResponse }; totals: ITotals } {
  const data: { [key: string]: IMappedShiftResponse } = {};
  const totals = emptyTotals();
  arr.forEach(({ date, shifts }) => {
    const shiftsMapByDay: IMappedShiftResponse = { shifts: {}, shifts_ids: [] };
    shifts.forEach((shift) => {
      shiftsMapByDay.shifts[shift.id] = { ...shift, _type };
      if (shift.sent_to_agencies) totals.sent_to_agencies += 1;
      if (shift.status === 'pending' && shift.current_status === 'Unfulfilled') {
        totals.statuses.unfulfilled += 1;
      }

      if (['assigned', 'accepted'].includes(shift.status) && shift.current_status === 'Completed') {
        totals.statuses.completed += 1;
      } else {
        totals.statuses[shift.status] += 1;
      }
      shiftsMapByDay.shifts_ids.push(shift.id);
    });

    data[DateTime.fromISO(date).startOf('day').toString()] = shiftsMapByDay;
  });
  return { data, totals };
}

function janeArrToJaneMap(
  arr: IPublishedShiftResponse[],
  _type: ShiftCategory,
): { data: { [key: string]: IDynamicMappedShiftResponse }; totals: ITotals } {
  const data: {
    [key: string]: IDynamicMappedShiftResponse;
  } = {};
  const totals = emptyTotals();
  arr.forEach(({ jane_id, day_schedules }) => {
    let numShifts = 0;
    data[jane_id] = {
      numShifts: 0,
    };
    day_schedules.forEach(({ date, shifts }) => {
      const shiftsMapByDay: IMappedShiftResponse = { shifts: {}, shifts_ids: [] };
      shifts.forEach((shift) => {
        // The v4 /internal endpoint exposes jane_id on the wrapper but not on each shift.
        // Copy/paste and other consumers expect shift.jane_id to be set, so attach it here.
        shiftsMapByDay.shifts[shift.id] = { ...shift, jane_id, _type };
        if (shift.sent_to_agencies) totals.sent_to_agencies += 1;

        if (shift.status === 'assigned' && shift.current_status === 'In Progress') {
          totals.statuses.in_progress += 1;
        } else if (shift.status === 'pending' && shift.current_status === 'Unfulfilled') {
          totals.statuses.unfulfilled += 1;
        }
        if (
          ['assigned', 'accepted'].includes(shift.status) &&
          shift.current_status === 'Completed'
        ) {
          totals.statuses.completed += 1;
        } else if (shift.no_show && shift.current_status === 'Absent') {
          totals.statuses.absent += 1;
        } else if (shift.current_status === 'Swapped') {
          totals.statuses.swapped += 1;
        } else {
          totals.statuses[shift.status] += 1;
        }
        shiftsMapByDay.shifts_ids.push(shift.id);
        numShifts += 1;
        data[jane_id].numShifts = numShifts;
      });

      data[jane_id][DateTime.fromISO(date).startOf('day').toString()] = shiftsMapByDay;
    });
  });
  return { data, totals };
}

export const GET_publishedShiftsOpenMapper = (
  apiResponseData: { open_shifts?: IDaySchedule[] } = { open_shifts: [] },
): IPublishedShiftsDayData => {
  return dayArrToDayMap(apiResponseData.open_shifts ?? [], TYPE_PUBLISHED_UNASSIGNED);
};

export const GET_publishedShiftsInternalMapper = (
  apiResponseData: { internal_team_member_shifts?: IPublishedShiftResponse[] } = {
    internal_team_member_shifts: [],
  },
): IPublishedShiftsJaneData => {
  return janeArrToJaneMap(
    apiResponseData.internal_team_member_shifts ?? [],
    TYPE_INTERNAL_TEAM_MEMBER,
  );
};

export const GET_publishedShiftsSharedMapper = (
  apiResponseData: { shared_team_member_shifts?: IDaySchedule[] } = {
    shared_team_member_shifts: [],
  },
): IPublishedShiftsDayData => {
  return dayArrToDayMap(apiResponseData.shared_team_member_shifts ?? [], TYPE_SHARED_TEAM_MEMBER);
};

export const GET_publishedShiftsAgencyMapper = (
  apiResponseData: { agency_team_member_shifts?: IDaySchedule[] } = {
    agency_team_member_shifts: [],
  },
): IPublishedShiftsDayData => {
  return dayArrToDayMap(apiResponseData.agency_team_member_shifts ?? [], TYPE_AGENCY_MEMBER);
};
